<template>
    <section class="faq-question-list">
        <!-- Loading State -->
        <div v-if="loading" class="faq-loading-container">
            <div class="faq-loader" :style="{ color: activeColor }">
                <i class="feather icon-loader animate-spin"></i>
                <p class="faq-loader-text text-onyx">Loading questions...</p>
            </div>
        </div>

        <!-- Questions List -->
        <vs-collapse v-else-if="questions.length > 0" accordion>
            <vs-collapse-item
                v-for="(question, index) in questions"
                :key="question.id || index"
                :class="[
                    'faq-question-item',
                    { 'faq-question-item-active': activeIndex === index },
                ]"
            >
                <div
                    slot="header"
                    :class="[
                        'faq-question-header rounded-lg text-base lg:text-20',
                        activeIndex === index
                            ? 'text-white'
                            : 'bg-white text-onyx',
                    ]"
                    :style="
                        activeIndex === index
                            ? { backgroundColor: activeColor }
                            : {}
                    "
                    @click="setActiveIndex(index)"
                >
                    <span class="faq-question-text">
                        {{ question.title }}
                    </span>
                    <span class="faq-question-icon">
                        <i
                            v-if="activeIndex !== index"
                            class="feather icon-plus text-onyx"
                        ></i>
                        <i v-else class="feather icon-minus text-white"></i>
                    </span>
                </div>
                <div class="faq-answer-content markdown leading-normal px-0">
                    <div class="ql-editor cdljn-custom">
                      <div v-html="question.answer"></div>
                    </div>
                </div>
            </vs-collapse-item>
        </vs-collapse>

        <!-- No Results State -->
        <div
            v-else
            class="flex justify-center items-center min-h-400 py-10 px-5"
        >
            <div
                class="flex flex-col items-center justify-center gap-4 text-center"
            >
                <i
                    class="feather icon-search text-24 text-onyx-lightest opacity-50"
                ></i>
                <p class="text-14 font-medium text-onyx">
                    No matching questions found
                </p>
                <p class="text-12 font-normal text-onyx-lightest mt-1">
                    Try adjusting your search keywords or browse by category
                </p>
            </div>
        </div>

        <!-- Load More Button -->
        <div v-if="hasMore && !loading" class="faq-load-more-container">
            <button
                @click="loadMore"
                :disabled="isLoading"
                class="faq-load-more-button w-full md:w-auto text-14"
                :style="{
                    backgroundColor: activeColor,
                    '--hover-bg-color': activeColorHover,
                }"
            >
                <span v-if="!isLoading">LOAD MORE</span>
                <span v-else class="faq-load-more-loader text-white">
                    <i class="feather icon-loader animate-spin"></i>
                </span>
            </button>
        </div>
    </section>
</template>

<script>
export default {
    name: 'FAQQuestionList',
    props: {
        questions: {
            type: Array,
            required: true,
            default: () => [],
        },
        hasMore: {
            type: Boolean,
            default: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        activeColor: {
            type: String,
            default: '#b32026',
        },
    },
    data() {
        return {
            activeIndex: 0,
            isLoading: false,
        };
    },
    computed: {
        activeColorHover() {
            // Darken the color by 10% for hover effect
            const hex = this.activeColor.replace('#', '');
            const r = parseInt(hex.substr(0, 2), 16);
            const g = parseInt(hex.substr(2, 2), 16);
            const b = parseInt(hex.substr(4, 2), 16);
            const darkenedR = Math.max(0, Math.floor(r * 0.9));
            const darkenedG = Math.max(0, Math.floor(g * 0.9));
            const darkenedB = Math.max(0, Math.floor(b * 0.9));
            return `#${darkenedR.toString(16).padStart(2, '0')}${darkenedG
                .toString(16)
                .padStart(2, '0')}${darkenedB.toString(16).padStart(2, '0')}`;
        },
    },
    mounted() {
        // Sync with vs-collapse's active state and open first item
        this.openFirstQuestion();
        this.setupObservers();
    },
    watch: {
        loading(newVal, oldVal) {
            // When loading completes (goes from true to false), open first question
            if (oldVal === true && newVal === false) {
                this.$nextTick(() => {
                    this.openFirstQuestion();
                });
            }
        },
        questions() {
            // When questions change, open first question
            if (!this.loading && this.questions.length > 0) {
                this.$nextTick(() => {
                    this.openFirstQuestion();
                });
            }
        },
    },
    methods: {
        openFirstQuestion() {
            // Programmatically open the first collapse item by clicking our header
            this.$nextTick(() => {
                setTimeout(() => {
                    const firstHeader = this.$el.querySelector(
                        '.faq-question-header',
                    );
                    if (firstHeader) {
                        firstHeader.click();
                    }
                    this.updateActiveState();
                }, 100);
            });
        },
        setupObservers() {
            // Watch for DOM changes to track active state
            this.$nextTick(() => {
                const observer = new MutationObserver(() => {
                    this.updateActiveState();
                });

                const collapseItems =
                    this.$el.querySelectorAll('.vs-collapse-item');
                collapseItems.forEach(item => {
                    observer.observe(item, {
                        attributes: true,
                        attributeFilter: ['class'],
                    });
                });
            });
        },
        setActiveIndex(index) {
            // Set active index immediately on click
            this.activeIndex = index;
            // Also verify with DOM after a short delay
            setTimeout(() => {
                this.updateActiveState();
            }, 50);
        },
        updateActiveState() {
            // Check DOM state to sync with vs-collapse's internal state
            const collapseItems =
                this.$el.querySelectorAll('.vs-collapse-item');
            let foundActive = false;
            collapseItems.forEach((item, index) => {
                if (item.classList.contains('vs-collapse-item--active')) {
                    if (this.activeIndex !== index) {
                        this.activeIndex = index;
                    }
                    foundActive = true;
                }
            });
            // If no active item found in DOM but we have an activeIndex, sync it
            if (!foundActive && this.activeIndex !== null) {
                // Keep the activeIndex as is if accordion allows none to be open
            }
        },
        async loadMore() {
            if (this.isLoading) return;

            this.isLoading = true;

            // Emit event to parent to load more questions
            // The parent will handle adding questions to the array
            this.$emit('load-more', () => {
                // Callback to reset loading state when parent is done
                this.isLoading = false;
            });

            // Fallback: reset loading after 2 seconds if parent doesn't call callback
            // Remove this if parent handles loading state properly
            setTimeout(() => {
                this.isLoading = false;
            }, 2000);
        },
    },
};
</script>

<style>
@import '~quill/dist/quill.core.css';
@import '~quill/dist/quill.snow.css';

.ql-editor.cdljn-custom {
    padding: 0;
    line-height: 26px;
    overflow: hidden !important;
}

.ql-editor.cdljn-custom ul,
.ql-editor.cdljn-custom ol {
    padding-left: 0 !important;
    margin-left: 40px;
}
.ql-editor.cdljn-custom ul > li::before {
    font-weight: 900;
}
.ql-editor.cdljn-custom ul > li::before,
.ql-editor.cdljn-custom ol > li::before {
    text-align: center;
}
.ql-editor.cdljn-custom ul > li,
.ql-editor.cdljn-custom ol > li {
    padding-left: 0;
}
.ql-editor li.ql-indent-1:not(.ql-direction-rtl) {
    padding-left: 2.5em !important;
}
</style>

<style scoped>
.faq-question-list {
    width: 100%;
}

.faq-question-list ::v-deep .vs-collapse {
    padding: 0 !important;
}

.faq-question-list ::v-deep .vs-collapse-item--header {
  padding: 14px 0 0 !important;
}

.vs-collapse-item.faq-question-item {
    border-bottom: 1px solid rgba(217, 217, 217, 0.5) !important;
}

.vs-collapse-item.faq-question-item:last-child {
    border-bottom: none !important;
}

.faq-question-item:first-child {
    border-top: none;
}

.faq-question-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 12px 16px;
    font-weight: 600;
    transition: all 0.3s ease;
    cursor: pointer;
}

.faq-question-text {
    flex: 1;
}

.faq-question-icon {
    margin-left: 16px;
    font-size: 20px;
    display: flex;
    align-items: center;
}

.faq-answer-content {
    color: #202028;
    font-size: 16px;
    line-height: 1.6;
    background-color: white;
}

/* Restore list styles for content rendered via v-html */
.faq-answer-content ::v-deep ul {
    list-style-type: disc !important;
    margin: 1em 0 !important;
    padding-left: 40px !important;
}

.faq-answer-content ::v-deep ol {
    list-style-type: decimal !important;
    margin: 1em 0 !important;
    padding-left: 40px !important;
}

.faq-answer-content ::v-deep li {
    margin: 0.5em 0;
    line-height: 1.6;
}

/* Override vs-collapse default styles */
.faq-question-list ::v-deep .vs-collapse-item {
    border: none;
    margin: 0;
}

.faq-question-list ::v-deep .vs-collapse-item .vs-collapse-item__header {
    padding: 0;
    border: none;
    background-color: transparent !important;
}

.faq-question-list ::v-deep .vs-collapse-item .vs-collapse-item__content {
    padding: 0;
    border: none;
}

/* Hide default vs-collapse arrow icon */
.faq-question-list ::v-deep .vs-collapse-item .icon-header,
.faq-question-list ::v-deep .vs-collapse-item .vs-icon,
.faq-question-list ::v-deep .vs-collapse-item .con-icon {
    display: none !important;
}

/* Loading State */
.faq-loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    padding: 40px 20px;
}

.faq-loader {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
}

.faq-loader i {
    font-size: 48px;
}

.faq-loader-text {
    font-size: 16px;
    font-weight: 500;
}

/* Load More Button */
.faq-load-more-container {
    display: flex;
    justify-content: center;
    margin-top: 48px;
    padding-top: 24px;
}

.faq-load-more-button {
    color: white;
    border: none;
    border-radius: 8px;
    padding: 16px 50px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.faq-load-more-button:hover:not(:disabled) {
    background-color: var(--hover-bg-color) !important;
    transform: translateY(-1px);
}

.faq-load-more-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.faq-load-more-loader {
    display: flex;
    align-items: center;
    justify-content: center;
}

.faq-load-more-loader i {
    font-size: 20px;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}
</style>
